/** @jsxRuntime classic */
/** @jsx jsx */
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { jsx } from "@emotion/core";
import { EditorState, Modifier, RichUtils } from 'draft-js';
import Editor from '@draft-js-plugins/editor';
import createInlineToolbarPlugin from '@draft-js-plugins/inline-toolbar';
import '@draft-js-plugins/inline-toolbar/lib/plugin.css';
import _ from 'lodash';
import {
  ItalicButton,
  BoldButton,
  UnderlineButton,
  UnorderedListButton,
  OrderedListButton,
} from '@draft-js-plugins/buttons';
import { hasChildTextNode, parseTodoistTextToHTML } from '../action/utilities';
import { stateFromHTML } from 'draft-js-import-html';

const BasicEditor = forwardRef(({id='basic-editor', content = '', handleKeyPress = () => {}, onChange = () => {}, placeholder = 'Write your thoughts here...'}, ref) => {
  const editorRef = useRef(null);
  useImperativeHandle(ref, () => ({
    description: editorRef.current
  }));
  const [open, setOpen] = useState(false);
  const [popup, setPopup] = useState(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const popupElement = document.querySelector('.edit-link-popup');
      if (popupElement && !popupElement.contains(event.target)) {
        setPopup(null);
      }
    };
  
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Task description plugins
  const { DescriptionInlineToolbar, descriptionPlugins } = useMemo(() => {
    const inlineToolbarPlugin = createInlineToolbarPlugin();
    const eventPlugin = {
      keyBindingFn: (e, editorState) => {
        const selection = editorState.getEditorState().getSelection();
        const currentContent = editorState.getEditorState().getCurrentContent();
        const blockKey = selection.getStartKey();
        const block = currentContent.getBlockForKey(blockKey);
        const blockText = block.getText();
        const blockLength = blockText.length;

        // if (e.key === 'Enter' && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
        //   e.preventDefault();
        //   return 'not-handled';
        // }
        // Add key bindings for formatting shortcuts
        if (e.ctrlKey || e.metaKey) {
          if (e.key === 'b') {
            return 'bold';
          }
          if (e.key === 'i') {
            return 'italic';
          }
          if (e.key === 'u') {
            return 'underline';
          }
        }
        // Convert "-" to unordered list only at the start of a new line
        if (blockLength === 0 || selection.getAnchorOffset() === 0) {
          if (e.key === '-') {
            return 'unordered-list';
          }
        }
        // Convert "1." to ordered list only at the start of a new line
        if (blockLength === 1 || selection.getAnchorOffset() === 1) {
          if (e.key === '.' && blockText === '1') {
            setTimeout(() => {
              // Clear the block text after converting to ordered list
            const contentState = editorState.getEditorState().getCurrentContent();
            const selection = editorState.getEditorState().getSelection();
            const newContentState = Modifier.replaceText(
              contentState,
              selection.merge({
                anchorOffset: 0,
                focusOffset: blockText.length
              }),
              ''
            );
            editorState.setEditorState(EditorState.push(
              editorState.getEditorState(),
              newContentState,
              'remove-range'
            ));
            }, 0);
            return 'ordered-list';
          }
        }
      }
    };
    // eslint-disable-next-line no-shadow
    const plugins = [inlineToolbarPlugin, eventPlugin];
    return { descriptionPlugins: plugins, DescriptionInlineToolbar: inlineToolbarPlugin.InlineToolbar };
  }, []);
  
  const [descriptionEditorState, setDescriptionEditorState] = useState(() => {
    if (content) {
      const contentState = stateFromHTML(parseTodoistTextToHTML(content));
      return EditorState.createWithContent(contentState);
    }
    return EditorState.createEmpty();
  });

  useEffect(() => {
    if (content) {
      const contentState = stateFromHTML(parseTodoistTextToHTML(content));
      setDescriptionEditorState(EditorState.createWithContent(contentState));
    }
  }, [content]);

  // Simple focus effect
  useEffect(() => {
    if (editorRef?.current) {
      setTimeout(() => {
        editorRef.current.focus();
      }, 0);
    }
  }, []);

  const adjustToolbarPosition = () => {
    const scrollTop = document?.getElementById('meeting-form')?.scrollTop ?? document?.getElementById('tasks-list-wrapper')?.scrollTop ?? 0;
    document.querySelectorAll('.tukdd6b').forEach(element => {
      element.style.marginTop = `${scrollTop}px`
    })
  }

  const handleDescriptionKeyCommand = (command) => {
    if (command === 'bold') {
      setDescriptionEditorState(RichUtils.toggleInlineStyle(descriptionEditorState, 'BOLD'));
      return 'handled';
    }
    if (command === 'italic') {
      setDescriptionEditorState(RichUtils.toggleInlineStyle(descriptionEditorState, 'ITALIC'));
      return 'handled';
    }
    if (command === 'underline') {
      setDescriptionEditorState(RichUtils.toggleInlineStyle(descriptionEditorState, 'UNDERLINE'));
      return 'handled';
    }
    if (command === 'ordered-list') {
      setDescriptionEditorState(RichUtils.toggleBlockType(descriptionEditorState, 'ordered-list-item'));
      return 'handled';
    }
    if (command === 'unordered-list') {
      setDescriptionEditorState(RichUtils.toggleBlockType(descriptionEditorState, 'unordered-list-item'));
      return 'handled';
    }
    return 'not-handled';
  };

  return (
    <div className='flex flex-col gap-[15px] task-formatting' id={id} css={{
      '.public-DraftEditor-content': {
        a: {
          color: `var(--accent-color)`
        }
      }
    }}>
        <div
          className="relative h-full"
          css={{
            '.DraftEditor-editorContainer':{
              opacity: 0.7
            }
          }}
          onKeyDown={e => {
            handleKeyPress(e)
          }}
        >
          <div
              className="editor-placeholder"
              css={{
                opacity: 0.5,
                display: hasChildTextNode(descriptionEditorState.getCurrentContent().getPlainText(), descriptionEditorState.getCurrentContent().getFirstBlock().getType()) ? 'none' : 'flex'
              }}
            >
            {placeholder}
          </div>
          <Editor
            editorKey={'editorDescription'}
            editorState={descriptionEditorState}
            handleKeyCommand={handleDescriptionKeyCommand}
            onChange={(editorState) => {
              setDescriptionEditorState(editorState);
            }}
            plugins={descriptionPlugins}
            ref={editorRef}
          />
          <DescriptionInlineToolbar>
            {
              (externalProps) => {
                adjustToolbarPosition();
                return (
                  <div className='tukdd6b-inner'>
                    <BoldButton {...externalProps} />
                    <ItalicButton {...externalProps} />
                    <UnderlineButton {...externalProps} />
                    <UnorderedListButton {...externalProps} />
                    <OrderedListButton {...externalProps} />
                  </div>
                )
              }
            }
          </DescriptionInlineToolbar>   
        </div>
    </div>
  );
});

export default BasicEditor;